import { useEffect, useState } from "react";
import adaptiveSecurityService from "../../api/adaptiveSecurityService";
import trustEventService from "../../api/trustEventService";

export default function AdaptiveSecurityPage() {
    const [userId, setUserId] = useState(1);
    const [ipAddress, setIpAddress] = useState("192.168.1.10");
    const [events, setEvents] = useState([]);
    const [history, setHistory] = useState([]);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        loadHistory();
    }, []);

    const loadHistory = async () => {
        const data = await trustEventService.getAll();
        setHistory(Array.isArray(data) ? data : []);
    };

    const addEvent = (type) => {
        const eventMap = {
            LOGIN_SUCCESS: ["LOGIN", "Successful login", true, 0],
            LOGIN_FAILED: ["LOGIN", "Failed login", false, 10],
            TWO_FACTOR_SUCCESS: ["TWO_FACTOR_SUCCESS", "Successful 2FA verification", true, 0],
            TWO_FACTOR_FAILED: ["TWO_FACTOR_FAILED", "Invalid 2FA code", false, 20],
            ANOMALY_DETECTED: ["ANOMALY_DETECTED", "AI anomaly detected suspicious activity", false, 30],
        };

        const [eventType, description, isSuccessful, riskScore] = eventMap[type];

        setEvents((prev) => [
            ...prev,
            {
                userId: Number(userId),
                eventType,
                description,
                ipAddress,
                isSuccessful,
                riskScore,
            },
        ]);
    };

    const evaluate = async () => {
        setError("");
        setResult(null);
        if (events.length === 0) {
            setError("Add at least one trust event.");
            return;
        }
        const data = await adaptiveSecurityService.evaluateAdaptiveSecurity(events);

        if (!data) {
            setError("Failed to evaluate adaptive security.");
            return;
        }

        setResult(data);

        for (const event of events) {
            await trustEventService.create(event);
        }

        await loadHistory();
    };

    const riskLabel = (risk) =>
        ({ 1: "Low", 2: "Medium", 3: "High", 4: "Critical" }[risk] || risk);

    return (
        <div className="max-w-6xl mx-auto p-6">
            <h2 className="text-2xl font-bold mb-4">Adaptive Security Dashboard</h2>

            <div className="border rounded p-4 mb-4">
                <input
                    type="number"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                    className="border p-2 mr-3"
                    placeholder="User ID"
                />

                <input
                    value={ipAddress}
                    onChange={(e) => setIpAddress(e.target.value)}
                    className="border p-2"
                    placeholder="IP Address"
                />
            </div>

            <div className="flex flex-wrap gap-3 mb-4">
                <button onClick={() => addEvent("LOGIN_SUCCESS")} className="bg-green-600 text-white px-4 py-2 rounded">
                    Login Success
                </button>
                <button onClick={() => addEvent("LOGIN_FAILED")} className="bg-red-600 text-white px-4 py-2 rounded">
                    Login Failed
                </button>
                <button onClick={() => addEvent("TWO_FACTOR_SUCCESS")} className="bg-green-700 text-white px-4 py-2 rounded">
                    2FA Success
                </button>
                <button onClick={() => addEvent("TWO_FACTOR_FAILED")} className="bg-orange-600 text-white px-4 py-2 rounded">
                    2FA Failed
                </button>
                <button onClick={() => addEvent("ANOMALY_DETECTED")} className="bg-purple-700 text-white px-4 py-2 rounded">
                    AI Anomaly
                </button>
            </div>

            <button onClick={evaluate} className="bg-blue-700 text-white px-4 py-2 rounded">
                Evaluate Trust Score
            </button>

            {error && <p className="text-red-500 mt-4">{error}</p>}

            {result && (
                <div className="border rounded p-4 mt-6 bg-gray-50">
                    <h3 className="text-xl font-bold mb-3">Result</h3>
                    <p><b>Trust Score:</b> {result.trustScore?.score}</p>
                    <p><b>Risk Level:</b> {riskLabel(result.trustScore?.riskLevel)}</p>
                    <p><b>Allow Access:</b> {result.decision?.allowAccess ? "Yes" : "No"}</p>
                    <p><b>Require 2FA:</b> {result.decision?.requireTwoFactor ? "Yes" : "No"}</p>
                    <p><b>Block Operation:</b> {result.decision?.blockOperation ? "Yes" : "No"}</p>
                    <p><b>Reason:</b> {result.decision?.reason}</p>
                </div>
            )}

            <h3 className="text-xl font-bold mt-8 mb-3">Trust Events History</h3>

            <table className="w-full border">
                <thead>
                    <tr>
                        <th className="border p-2">ID</th>
                        <th className="border p-2">User</th>
                        <th className="border p-2">Event</th>
                        <th className="border p-2">IP</th>
                        <th className="border p-2">Success</th>
                        <th className="border p-2">Risk</th>
                        <th className="border p-2">Date</th>
                    </tr>
                </thead>
                <tbody>
                    {history.map((e) => (
                        <tr key={e.id}>
                            <td className="border p-2">{e.id}</td>
                            <td className="border p-2">{e.userName || e.userId || "�"}</td>
                            <td className="border p-2">{e.eventType}</td>
                            <td className="border p-2">{e.ipAddress}</td>
                            <td className="border p-2">{e.isSuccessful ? "Yes" : "No"}</td>
                            <td className="border p-2">{e.riskScore}</td>
                            <td className="border p-2">
                                {e.eventDate ? new Date(e.eventDate).toLocaleString() : "�"}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}